import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

interface AppointmentDetailsTabsProps {
  /**
   * When false, there is only one panel of content, so no tab bar is shown —
   * the details content is returned bare. Driven by the WEB_PREVISIT_INSIGHTS
   * flag, hoisted to the parent.
   */
  insightsEnabled: boolean;
  detailsContent: React.ReactNode;
  insightsContent: React.ReactNode;
}

/**
 * Presentational shell for the appointment sidebar's Details | Insights tabs.
 * Pure layout — owns no GraphQL or feature-flag reads (the flag is hoisted to
 * the parent). With the flag off there is nothing to tab between, so the tab
 * bar is omitted entirely and the details content renders on its own.
 *
 * The Insights panel is `forceMount`ed so it stays in the DOM (hidden) while
 * Details is active: this pre-warms generation on load and preserves the
 * patient-change regeneration path in usePrevisitInsights (ENG-4601). Details
 * is the default tab — the flag must not change the default experience.
 */
export const AppointmentDetailsTabs: React.FC<AppointmentDetailsTabsProps> = ({
  insightsEnabled,
  detailsContent,
  insightsContent,
}) => {
  const { t } = useTranslation();

  if (!insightsEnabled) {
    return <>{detailsContent}</>;
  }

  return (
    <Tabs defaultValue="details">
      <TabsList variant="line">
        <TabsTrigger value="details" className="flex-none">
          {t('appointmentPage.details.title')}
        </TabsTrigger>
        <TabsTrigger value="insights" className="flex-none">
          {t('appointment.insightsTab')}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="details">{detailsContent}</TabsContent>
      {/* forceMount: start PVI generation on load, not when the user opens this tab */}
      <TabsContent value="insights" forceMount>
        {insightsContent}
      </TabsContent>
    </Tabs>
  );
};
